import { useCallback, useEffect, useState } from "react";
import { fetchRequests, fetchRequest } from "../api/client";
import type { ProcessingRequest, PaginatedResponse } from "../api/types";

export function useRequestList(params: {
  status?: string;
  page: number;
  size: number;
}) {
  const [data, setData] = useState<PaginatedResponse<ProcessingRequest> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchRequests(params)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params.status, params.page, params.size]);

  useEffect(() => {
    return load();
  }, [load]);

  return { data, isLoading, error, refresh: load };
}

export function useRequestDetail(id: string | undefined) {
  const [request, setRequest] = useState<ProcessingRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    let cancelled = false;
    setIsLoading(true);
    fetchRequest(id)
      .then((r) => {
        if (!cancelled) {
          setRequest(r);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    return load();
  }, [load]);

  return { request, isLoading, error, refresh: load };
}
